import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, Check, Mail, Phone, MapPin } from "lucide-react";
import { Eyebrow, FadeUp } from "../components/site/Primitives";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";
import { submitQuote } from "../lib/api";
import { useProducts } from "../hooks/useProducts";

const QUANTITY_OPTIONS = [
  "Under 1,000",
  "1,000 – 5,000",
  "5,000 – 20,000",
  "20,000 – 50,000",
  "50,000+",
  "Full container",
];

export default function ContactPage() {
  const [params] = useSearchParams();
  const initialProduct = params.get("product") || "";
  const initialSubject = params.get("subject") || "";
  const { products } = useProducts();

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    product_interest: initialProduct,
    monthly_quantity: "",
    private_label: initialSubject === "private-label",
    message: "",
    source_page: initialSubject ? `contact-${initialSubject}` : "contact",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitQuote(form);
      setSubmitted(true);
      toast.success("Thanks — your enquiry has been received.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email support@blacpac.shop.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0A0A09]">
      <section className="relative pt-12 pb-12 lg:pb-20 kraft-texture">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <FadeUp>
            <Eyebrow>Contact / Request a Quote</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="mt-8 font-grotesk text-5xl md:text-6xl lg:text-8xl font-black tracking-[-0.04em] leading-[0.92] text-[#F7F5F0] max-w-5xl">
              Let&rsquo;s build your{" "}
              <span className="font-serif-display italic font-medium text-[#E85D2F]">
                packaging supply.
              </span>
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-[#F7F5F0]/75 leading-relaxed font-light">
              Tell us about your business and what you need. We&rsquo;ll come back
              with a clear recommendation, sample options and indicative pricing.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-8">
            <FadeUp>
              <div className="rounded-3xl border border-[#E85D2F]/15 bg-[#121211] p-7">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#E85D2F] font-semibold">
                  Direct
                </p>
                <ul className="mt-6 space-y-5 text-[#F7F5F0]/85">
                  <li className="flex items-start gap-3">
                    <Mail className="h-5 w-5 mt-0.5 text-[#E85D2F] shrink-0" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#F7F5F0]/50">Email</p>
                      <a className="hover:text-[#E85D2F] transition-colors" href="mailto:support@blacpac.shop">
                        support@blacpac.shop
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Phone className="h-5 w-5 mt-0.5 text-[#E85D2F] shrink-0" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#F7F5F0]/50">Phone</p>
                      <p>+233 24 123 4567</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 mt-0.5 text-[#E85D2F] shrink-0" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#F7F5F0]/50">Office</p>
                      <p>Accra, Ghana</p>
                    </div>
                  </li>
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="rounded-3xl bg-[#1A1A18] p-7">
                <p className="font-serif-display italic text-[#E85D2F] text-sm">
                  Looking for...
                </p>
                <h3 className="mt-3 font-grotesk text-2xl font-bold text-[#F7F5F0] tracking-tight leading-tight">
                  Wholesale pricing? Private-label sleeves? Sample packs?
                </h3>
                <p className="mt-3 text-sm text-[#F7F5F0]/75 leading-relaxed">
                  Mention it in the message field — we&rsquo;ll route your enquiry to
                  the right person on the team.
                </p>
              </div>
            </FadeUp>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="quote-success"
                className="rounded-3xl border border-[#E85D2F]/30 bg-[#121211] p-10 md:p-14 text-center"
              >
                <div className="mx-auto h-16 w-16 rounded-full bg-[#E85D2F] flex items-center justify-center">
                  <Check className="h-7 w-7 text-[#0A0A09]" strokeWidth={2.5} />
                </div>
                <h2 className="mt-8 font-grotesk text-3xl md:text-4xl font-black tracking-tighter text-[#F7F5F0]">
                  Enquiry received.
                </h2>
                <p className="mt-4 text-[#F7F5F0]/70 max-w-md mx-auto font-light leading-relaxed">
                  Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}. A member of the BlacPac team will be in touch within 1–2 business days.
                </p>
                <button
                  data-testid="submit-another"
                  onClick={() => {
                    setSubmitted(false);
                    setForm({
                      name: "",
                      company: "",
                      email: "",
                      phone: "",
                      product_interest: "",
                      monthly_quantity: "",
                      private_label: false,
                      message: "",
                      source_page: "contact",
                    });
                  }}
                  className="mt-8 inline-flex items-center gap-3 rounded-full border border-[#E85D2F]/50 text-[#E85D2F] px-6 py-3 text-sm font-medium hover:bg-[#E85D2F] hover:text-[#0A0A09] transition-colors"
                >
                  Submit another enquiry
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="quote-form"
                className="rounded-3xl border border-[#E85D2F]/15 bg-[#121211] p-7 md:p-10 space-y-7"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-[#E85D2F] font-semibold">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="input-name"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      className="bg-transparent border-0 border-b border-[#E85D2F]/30 rounded-none px-0 text-[#F7F5F0] placeholder:text-[#F7F5F0]/30 focus-visible:ring-0 focus-visible:border-[#E85D2F] py-3 h-auto"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-xs uppercase tracking-[0.18em] text-[#E85D2F] font-semibold">
                      Company
                    </Label>
                    <Input
                      id="company"
                      data-testid="input-company"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Restaurant / brand name"
                      className="bg-transparent border-0 border-b border-[#E85D2F]/30 rounded-none px-0 text-[#F7F5F0] placeholder:text-[#F7F5F0]/30 focus-visible:ring-0 focus-visible:border-[#E85D2F] py-3 h-auto"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-[#E85D2F] font-semibold">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="input-email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@brand.com"
                      className="bg-transparent border-0 border-b border-[#E85D2F]/30 rounded-none px-0 text-[#F7F5F0] placeholder:text-[#F7F5F0]/30 focus-visible:ring-0 focus-visible:border-[#E85D2F] py-3 h-auto"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] text-[#E85D2F] font-semibold">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      data-testid="input-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+233 or +44…"
                      className="bg-transparent border-0 border-b border-[#E85D2F]/30 rounded-none px-0 text-[#F7F5F0] placeholder:text-[#F7F5F0]/30 focus-visible:ring-0 focus-visible:border-[#E85D2F] py-3 h-auto"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.18em] text-[#E85D2F] font-semibold">
                      Product interest
                    </Label>
                    <Select
                      value={form.product_interest}
                      onValueChange={(v) => setForm((f) => ({ ...f, product_interest: v }))}
                    >
                      <SelectTrigger
                        data-testid="select-product"
                        className="bg-transparent border-0 border-b border-[#E85D2F]/30 rounded-none px-0 text-[#F7F5F0] focus:ring-0 focus:border-[#E85D2F] py-3 h-auto"
                      >
                        <SelectValue placeholder="Select a product" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121211] border-[#E85D2F]/20 text-[#F7F5F0]">
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.name} className="focus:bg-[#1A1A18] focus:text-[#F7F5F0]">
                            {p.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="Mixed range / not sure" className="focus:bg-[#1A1A18] focus:text-[#F7F5F0]">
                          Mixed range / not sure
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.18em] text-[#E85D2F] font-semibold">
                      Monthly quantity estimate
                    </Label>
                    <Select
                      value={form.monthly_quantity}
                      onValueChange={(v) => setForm((f) => ({ ...f, monthly_quantity: v }))}
                    >
                      <SelectTrigger
                        data-testid="select-quantity"
                        className="bg-transparent border-0 border-b border-[#E85D2F]/30 rounded-none px-0 text-[#F7F5F0] focus:ring-0 focus:border-[#E85D2F] py-3 h-auto"
                      >
                        <SelectValue placeholder="Select an estimate" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121211] border-[#E85D2F]/20 text-[#F7F5F0]">
                        {QUANTITY_OPTIONS.map((q) => (
                          <SelectItem key={q} value={q} className="focus:bg-[#1A1A18] focus:text-[#F7F5F0]">
                            {q}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-2xl bg-[#0A0A09] border border-[#E85D2F]/15 px-5 py-4">
                  <div>
                    <Label htmlFor="private-label" className="text-sm text-[#F7F5F0] font-medium">
                      Private label required?
                    </Label>
                    <p className="text-xs text-[#F7F5F0]/55 mt-1">
                      Custom prints, sleeves, branded stickers.
                    </p>
                  </div>
                  <Switch
                    id="private-label"
                    data-testid="switch-private-label"
                    checked={form.private_label}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, private_label: v }))}
                    className="data-[state=checked]:bg-[#E85D2F]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-[#E85D2F] font-semibold">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Tell us about your menu, current packaging, launch date or any artwork you have ready."
                    className="bg-transparent border border-[#E85D2F]/20 rounded-2xl px-4 py-3 text-[#F7F5F0] placeholder:text-[#F7F5F0]/30 focus-visible:ring-0 focus-visible:border-[#E85D2F]"
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4">
                  <p className="text-xs text-[#F7F5F0]/45 max-w-sm">
                    By submitting this form you agree to be contacted by the BlacPac team about your enquiry.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-quote"
                    className="group inline-flex items-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-7 py-4 font-medium tracking-wide hover:bg-[#F47A4A] transition-colors disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Submit enquiry
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
